import { useEffect, useState } from 'react';
import { CalendarIcon, ChevronLeft, ChevronRight, Eye, Send, MessageCircle, XCircle, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getQuizResponses } from '@/lib/api';
import { toast } from 'sonner';
import { ru } from 'date-fns/locale';
import { format, subDays } from 'date-fns';

function CopyContactButton({ name, phone }) {
  const [copied, setCopied] = useState(false);
  const disabled = !name && !phone;

  const onClick = async (e) => {
    e.stopPropagation();
    if (disabled) return;
    const text = `${(name || '').trim()}\t${(phone || '').trim()}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Скопировано: имя + телефон');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_err) {
      toast.error('Не удалось скопировать');
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? 'Нет данных' : 'Скопировать имя + телефон (через таб)'}
      data-testid="copy-contact-button"
      className={copied ? 'h-8 px-2 text-emerald-600' : 'h-8 px-2 text-slate-500 hover:text-emerald-700'}
    >
      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
    </Button>
  );
}

export default function AdminQuizResponses() {
  const [responses, setResponses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [busy, setBusy] = useState(true);
  const [selectedResponse, setSelectedResponse] = useState(null);
  
  // Date filter
  const [dateFilter, setDateFilter] = useState('today');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const currentDateWindow = () => {
    const today = new Date();
    const yesterday = subDays(today, 1);
    
    switch (dateFilter) {
      case 'today':
        return { startDate: format(today, 'yyyy-MM-dd'), endDate: format(today, 'yyyy-MM-dd') };
      case 'yesterday':
        return { startDate: format(yesterday, 'yyyy-MM-dd'), endDate: format(yesterday, 'yyyy-MM-dd') };
      case 'custom':
        if (dateRange.from) {
          return {
            startDate: format(dateRange.from, 'yyyy-MM-dd'),
            endDate: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : format(dateRange.from, 'yyyy-MM-dd')
          };
        }
        return { startDate: null, endDate: null };
      default:
        return { startDate: null, endDate: null };
    }
  };

  const currentDateLabel = () => {
    switch (dateFilter) {
      case 'today': return 'Сегодня';
      case 'yesterday': return 'Вчера';
      case 'custom':
        if (dateRange.from) {
          if (dateRange.to && dateRange.from !== dateRange.to) {
            return `${format(dateRange.from, 'dd.MM')} - ${format(dateRange.to, 'dd.MM')}`;
          }
          return format(dateRange.from, 'dd.MM.yyyy');
        }
        return 'Выбрать даты';
      default: return 'Все время';
    }
  };

  useEffect(() => {
    loadData();
  }, [page, dateFilter, dateRange]);

  const loadData = async () => {
    try {
      setBusy(true);
      const { startDate, endDate } = currentDateWindow();
      const res = await getQuizResponses(page, 20, startDate, endDate);
      setResponses(res.data.responses);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error('Failed to load responses:', err);
    } finally {
      setBusy(false);
    }
  };

  const applyDatePreset = (filter) => {
    setDateFilter(filter);
    setPage(1);
    if (filter !== 'custom') {
      setDateRange({ from: null, to: null });
    }
  };

  const toShortDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('ru-RU', { 
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-quiz-responses">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ответы на квиз</h1>
          <p className="text-slate-500">Всего ответов: {total}</p>
        </div>
        
        {/* Date Filter */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={dateFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => applyDatePreset('all')}
          >
            Все время
          </Button>
          <Button
            variant={dateFilter === 'today' ? 'default' : 'outline'}
            size="sm"
            onClick={() => applyDatePreset('today')}
          >
            Сегодня
          </Button>
          <Button
            variant={dateFilter === 'yesterday' ? 'default' : 'outline'}
            size="sm"
            onClick={() => applyDatePreset('yesterday')}
          >
            Вчера
          </Button>
          
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={dateFilter === 'custom' ? 'default' : 'outline'}
                size="sm"
                className="min-w-[140px]"
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                {dateFilter === 'custom' && dateRange.from ? currentDateLabel() : 'Произвольно'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={(range) => {
                  setDateRange(range || { from: null, to: null });
                  if (range?.from) {
                    setDateFilter('custom');
                    setPage(1);
                  }
                  if (range?.to) setCalendarOpen(false);
                }}
                locale={ru}
                disabled={(date) => date > new Date()}
                numberOfMonths={1}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Responses Table */}
      <Card>
        <CardContent className="pt-6">
          {busy ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : responses.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Нет ответов за выбранный период</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-slate-500">Дата</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-500">Имя</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-500">Телефон</th>
                      <th className="text-center py-2 px-1 font-medium text-slate-500 w-10"></th>
                      <th className="text-center py-2 px-2 font-medium text-slate-500">Возраст</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-500">Страна</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-500">IP</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-500">Устройство</th>
                      <th className="text-center py-2 px-2 font-medium text-slate-500" title="Всего заходов с этой сессии">Заходов</th>
                      <th className="text-left py-2 px-2 font-medium text-slate-500">Ответов</th>
                      <th className="text-center py-2 px-2 font-medium text-slate-500">Написал</th>
                      <th className="text-center py-2 px-2 font-medium text-slate-500">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {responses.map((r, i) => (
                      <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-2 text-slate-600">{toShortDateTime(r.completed_at)}</td>
                        <td className="py-2 px-2 font-medium text-slate-900">{r.visitor_name || '-'}</td>
                        <td className="py-2 px-2 text-slate-700 font-mono text-xs">
                          {r.visitor_phone ? (
                            <a href={`tel:${r.visitor_phone}`} className="text-emerald-700 hover:underline">
                              {r.visitor_phone}
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="py-2 px-1 text-center">
                          <CopyContactButton name={r.visitor_name} phone={r.visitor_phone} />
                        </td>
                        <td className="py-2 px-2 text-center">
                          {(() => {
                            const ageAns = r.answers?.find(a => /сколько.*лет|возраст/i.test(a.question_text || ''));
                            return ageAns?.answer_text ? (
                              <span className="inline-flex items-center justify-center px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                {ageAns.answer_text}
                              </span>
                            ) : '-';
                          })()}
                        </td>
                        <td className="py-2 px-2">
                          <span className="text-slate-900">{r.country || 'Unknown'}</span>
                          {r.city && <span className="text-slate-400 text-xs ml-1">{r.city}</span>}
                        </td>
                        <td className="py-2 px-2 text-slate-500 font-mono text-xs">{r.ip || '-'}</td>
                        <td className="py-2 px-2 text-slate-600">{r.device || 'Unknown'}</td>
                        <td className="py-2 px-2 text-center">
                          {r.visits > 1 ? (
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] px-2 h-6 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                              {r.visits}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">{r.visits || 1}</span>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          <span className="inline-flex items-center justify-center px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                            {r.answers?.length || 0}
                          </span>
                        </td>
                        <td className="py-2 px-2">
                          <div className="flex flex-wrap items-center justify-center gap-1.5 max-w-[280px]">
                            {(r.telegram_clicks > 0 || r.whatsapp_clicks > 0) ? (
                              <>
                                {(r.telegram_managers?.length ? r.telegram_managers : (r.telegram_clicks > 0 ? [{ name: '', count: r.telegram_clicks }] : [])).map((m, idx) => (
                                  <span
                                    key={`tg-${idx}`}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-sky-100 text-sky-700 rounded-md text-xs font-medium"
                                    title={`Клики по Telegram${m.name ? ' — ' + m.name : ''}: ${m.count}`}
                                  >
                                    <Send className="w-3 h-3" />
                                    {m.name && <span>{m.name}</span>}
                                    <span className="font-mono">×{m.count}</span>
                                  </span>
                                ))}
                                {(r.whatsapp_managers?.length ? r.whatsapp_managers : (r.whatsapp_clicks > 0 ? [{ name: '', count: r.whatsapp_clicks }] : [])).map((m, idx) => (
                                  <span
                                    key={`wa-${idx}`}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs font-medium"
                                    title={`Клики по WhatsApp${m.name ? ' — ' + m.name : ''}: ${m.count}`}
                                  >
                                    <MessageCircle className="w-3 h-3" />
                                    {m.name && <span>{m.name}</span>}
                                    <span className="font-mono">×{m.count}</span>
                                  </span>
                                ))}
                              </>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-md text-xs font-medium"
                                title="Заполнил анкету, но не написал ни одному менеджеру"
                              >
                                <XCircle className="w-3 h-3" />
                                Нет
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedResponse(r)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    Страница {page} из {pages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(pages, p + 1))}
                      disabled={page === pages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Response Detail Dialog */}
      <Dialog open={!!selectedResponse} onOpenChange={() => setSelectedResponse(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ответы посетителя</DialogTitle>
          </DialogHeader>
          
          {selectedResponse && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-slate-500">Дата:</div>
                <div>{toShortDateTime(selectedResponse.completed_at)}</div>
                <div className="text-slate-500">Имя:</div>
                <div className="font-medium">{selectedResponse.visitor_name || '—'}</div>
                <div className="text-slate-500">Телефон:</div>
                <div className="font-mono text-xs flex items-center gap-2">
                  {selectedResponse.visitor_phone ? (
                    <a href={`tel:${selectedResponse.visitor_phone}`} className="text-emerald-700 hover:underline">
                      {selectedResponse.visitor_phone}
                    </a>
                  ) : '—'}
                  <CopyContactButton
                    name={selectedResponse.visitor_name}
                    phone={selectedResponse.visitor_phone}
                  />
                </div>
                <div className="text-slate-500">Страна:</div>
                <div>{selectedResponse.country} {selectedResponse.city && `(${selectedResponse.city})`}</div>
                <div className="text-slate-500">IP:</div>
                <div className="font-mono text-xs">{selectedResponse.ip}</div>
                <div className="text-slate-500">Устройство:</div>
                <div>{selectedResponse.device}</div>
                <div className="text-slate-500">Заходов:</div>
                <div>{selectedResponse.visits || 1}</div>
                
                {/* Age from answers */}
                {(() => {
                  const ageAnswer = selectedResponse.answers?.find(a => /сколько.*лет|возраст/i.test(a.question_text || ''));
                  if (ageAnswer?.answer_text) {
                    return (
                      <>
                        <div className="text-slate-500">Возраст:</div>
                        <div className="font-medium text-blue-700">{ageAnswer.answer_text} лет</div>
                      </>
                    );
                  }
                  return null;
                })()}
                
                {/* New fields: work type and vacancy */}
                {selectedResponse.work_type && (
                  <>
                    <div className="text-slate-500">Тип работы:</div>
                    <div className="font-medium text-emerald-700">{selectedResponse.work_type}</div>
                  </>
                )}
                {selectedResponse.selected_vacancy_title && (
                  <>
                    <div className="text-slate-500">Выбранная вакансия:</div>
                    <div className="font-medium text-emerald-700">{selectedResponse.selected_vacancy_title}</div>
                  </>
                )}
                {selectedResponse.selected_vacancy_country && (
                  <>
                    <div className="text-slate-500">Желаемая страна:</div>
                    <div className="font-medium">{selectedResponse.selected_vacancy_country}</div>
                  </>
                )}
                
                <div className="text-slate-500">Написал менеджеру:</div>
                <div className="flex flex-wrap gap-1.5">
                  {(selectedResponse.telegram_managers?.length ? selectedResponse.telegram_managers : (selectedResponse.telegram_clicks > 0 ? [{ name: '', count: selectedResponse.telegram_clicks }] : [])).map((m, i) => (
                    <span key={`tg-${i}`} className="inline-flex items-center gap-1 px-2 py-1 bg-sky-100 text-sky-700 rounded-md text-xs font-medium">
                      <Send className="w-3 h-3" />
                      Telegram{m.name ? ` · ${m.name}` : ''} · {m.count}
                    </span>
                  ))}
                  {(selectedResponse.whatsapp_managers?.length ? selectedResponse.whatsapp_managers : (selectedResponse.whatsapp_clicks > 0 ? [{ name: '', count: selectedResponse.whatsapp_clicks }] : [])).map((m, i) => (
                    <span key={`wa-${i}`} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs font-medium">
                      <MessageCircle className="w-3 h-3" />
                      WhatsApp{m.name ? ` · ${m.name}` : ''} · {m.count}
                    </span>
                  ))}
                  {!selectedResponse.telegram_clicks && !selectedResponse.whatsapp_clicks && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-md text-xs font-medium">
                      <XCircle className="w-3 h-3" />
                      Не написал
                    </span>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Ответы:</h4>
                <div className="space-y-3">
                  {selectedResponse.answers?.map((a, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3">
                      <p className="text-sm text-slate-500 mb-1">#{i + 1} {a.question_text}</p>
                      <p className="font-medium">{a.answer_text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
